import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Trash2, User } from 'lucide-react';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const emptySubscription = () => ({
  plan_id: '',
  is_free: false,
  start_date: new Date().toISOString().split('T')[0],
  expires_at: '',
  renewal_count: '',
  auto_renew: false,
  notes: ''
});

const titleFor = (item) => (
  item.title ||
  item.translations?.el?.title ||
  item.translations?.en?.title ||
  item.name ||
  item.slug ||
  item.id
);

const UserEditDialog = ({ open, onClose, userId, token, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [plans, setPlans] = useState([]);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_language: 'el',
    role: 'user',
    is_active: true,
    birthday_day: '',
    birthday_month: '',
    birthday_year: '',
    nameday_name: '',
    nameday_day: '',
    nameday_month: '',
    nameday_confirmed: false,
    nameday_date_rule: null,
    nameday_options: []
  });
  const [categoryAccess, setCategoryAccess] = useState({});
  const [videoAccess, setVideoAccess] = useState({});
  const [accessExpiresAt, setAccessExpiresAt] = useState('');
  const [subscriptionForm, setSubscriptionForm] = useState(emptySubscription());
  const [analytics, setAnalytics] = useState(null);
  const [namedayLookup, setNamedayLookup] = useState(null);

  useEffect(() => {
    if (open && userId) {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [userRes, categoriesRes, videosRes, plansRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/users/${userId}`, { headers }),
        axios.get(`${API}/categories?locale=el`, { headers }),
        axios.get(`${API}/admin/videos`, { headers }),
        axios.get(`${API}/admin/plans`, { headers }),
        axios.get(`${API}/admin/users/${userId}/analytics?locale=el`, { headers })
      ]);

      const detail = userRes.data || {};
      const user = detail.user || {};
      setUserData(detail);
      setCategories(categoriesRes.data || []);
      setVideos(videosRes.data || []);
      setPlans(plansRes.data || []);
      setAnalytics(analyticsRes.data || null);
      setUserForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        preferred_language: user.preferred_language || 'el',
        role: user.role || 'user',
        is_active: user.is_active !== false,
        birthday_day: user.birthday_day || '',
        birthday_month: user.birthday_month || '',
        birthday_year: user.birthday_year || '',
        nameday_name: user.nameday_name || user.name?.split(' ')[0] || '',
        nameday_day: user.nameday_day || '',
        nameday_month: user.nameday_month || '',
        nameday_confirmed: !!user.nameday_confirmed,
        nameday_date_rule: user.nameday_date_rule || null,
        nameday_options: user.nameday_options || []
      });

      const categoryMap = {};
      const videoMap = {};
      (detail.entitlements || []).forEach((grant) => {
        if (grant.access_type === 'category') categoryMap[grant.target_id] = true;
        if (grant.access_type === 'video') videoMap[grant.target_id] = true;
      });
      setCategoryAccess(categoryMap);
      setVideoAccess(videoMap);

      if (detail.subscription) {
        setSubscriptionForm({
          plan_id: detail.subscription.plan_id || '',
          is_free: !!detail.subscription.is_free,
          start_date: detail.subscription.start_date?.split('T')[0] || '',
          expires_at: detail.subscription.expires_at?.startsWith('9999-') ? '' : (detail.subscription.expires_at?.split('T')[0] || ''),
          renewal_count: detail.subscription.renewal_count ?? '',
          auto_renew: !!detail.subscription.auto_renew,
          notes: detail.subscription.notes || ''
        });
      } else {
        setSubscriptionForm(emptySubscription());
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    onClose?.(isOpen);
  };

  const saveUser = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/users/${userId}`, {
        ...userForm,
        birthday_day: userForm.birthday_day ? Number(userForm.birthday_day) : null,
        birthday_month: userForm.birthday_month ? Number(userForm.birthday_month) : null,
        birthday_year: userForm.birthday_year ? Number(userForm.birthday_year) : null,
        nameday_day: userForm.nameday_day ? Number(userForm.nameday_day) : null,
        nameday_month: userForm.nameday_month ? Number(userForm.nameday_month) : null,
        nameday_source: userForm.nameday_confirmed ? 'admin_set' : 'unknown'
      }, { headers });
      toast.success('User saved');
      onUpdate?.();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const lookupNameday = async () => {
    if (!userForm.nameday_name) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/namedays/search?name=${encodeURIComponent(userForm.nameday_name)}`, { headers });
      setNamedayLookup(res.data);
      const options = res.data?.options || [];
      if (options.length === 1) {
        setUserForm({
          ...userForm,
          nameday_day: options[0].day,
          nameday_month: options[0].month,
          nameday_confirmed: true,
          nameday_source: 'admin_selected',
          nameday_date_rule: options[0],
          nameday_options: options
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nameday lookup failed');
    }
  };

  const toggleCategory = (categoryId, checked) => {
    setCategoryAccess((prev) => ({ ...prev, [categoryId]: checked }));
    setVideoAccess((prev) => {
      const next = { ...prev };
      videos.filter((video) => video.category_id === categoryId).forEach((video) => {
        next[video.id] = checked;
      });
      return next;
    });
  };

  const saveAccess = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/users/${userId}/access`, {
        categories: Object.entries(categoryAccess).filter(([, selected]) => selected).map(([id]) => id),
        videos: Object.entries(videoAccess).filter(([, selected]) => selected).map(([id]) => id),
        expires_at: accessExpiresAt || null
      }, { headers });
      toast.success('Access saved');
      onUpdate?.();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save access');
    } finally {
      setSaving(false);
    }
  };

  const saveSubscription = async () => {
    if (!subscriptionForm.plan_id) {
      toast.error('Select a plan');
      return;
    }
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/users/${userId}/subscription`, {
        ...subscriptionForm,
        renewal_count: subscriptionForm.renewal_count === '' ? null : parseInt(subscriptionForm.renewal_count, 10)
      }, { headers });
      toast.success('Subscription saved');
      onUpdate?.();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Cancel this manual subscription?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/users/${userId}/subscription`, { headers });
      toast.success('Subscription cancelled');
      setSubscriptionForm(emptySubscription());
      onUpdate?.();
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const paidSubscriptionReadOnly = userData?.subscription?.provider && userData.subscription.provider !== 'admin_manual';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-misel-card border-white/10 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-roboto font-bold flex items-center gap-2">
            <User size={20} className="text-primary" />
            Edit User {userData?.user?.email ? `- ${userData.user.email}` : ''}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-gray-400">Loading...</div>
        ) : (
          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="grid grid-cols-5 bg-white/5">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="access">Access</TabsTrigger>
              <TabsTrigger value="subscription">Subscription</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-6">
              <div className="grid grid-cols-2 gap-4">
                <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/10 text-white" />
                <Input value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="Email" className="bg-white/5 border-white/10 text-white" />
                <Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} placeholder="Phone" className="bg-white/5 border-white/10 text-white" />
                <select value={userForm.preferred_language} onChange={(e) => setUserForm({ ...userForm, preferred_language: e.target.value })} className="bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2">
                  <option value="el">Greek</option>
                  <option value="en">English</option>
                </select>
                <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2">
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
                <select value={userForm.is_active ? 'active' : 'inactive'} onChange={(e) => setUserForm({ ...userForm, is_active: e.target.value === 'active' })} className="bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-md p-4 space-y-3">
                <h3 className="text-white font-medium">Birthday</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Input type="number" min="1" max="31" value={userForm.birthday_day} onChange={(e) => setUserForm({ ...userForm, birthday_day: e.target.value })} placeholder="Day" className="bg-white/5 border-white/10 text-white" />
                  <Input type="number" min="1" max="12" value={userForm.birthday_month} onChange={(e) => setUserForm({ ...userForm, birthday_month: e.target.value })} placeholder="Month" className="bg-white/5 border-white/10 text-white" />
                  <Input type="number" min="1900" max="2100" value={userForm.birthday_year} onChange={(e) => setUserForm({ ...userForm, birthday_year: e.target.value })} placeholder="Year optional" className="bg-white/5 border-white/10 text-white" />
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-md p-4 space-y-3">
                <h3 className="text-white font-medium">Nameday</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Input value={userForm.nameday_name} onChange={(e) => setUserForm({ ...userForm, nameday_name: e.target.value })} placeholder="Name" className="bg-white/5 border-white/10 text-white" />
                  <Button type="button" variant="outline" className="border-white/20" onClick={lookupNameday}>Lookup</Button>
                  <Input type="number" min="1" max="31" value={userForm.nameday_day} onChange={(e) => setUserForm({ ...userForm, nameday_day: e.target.value })} placeholder="Day" className="bg-white/5 border-white/10 text-white" />
                  <Input type="number" min="1" max="12" value={userForm.nameday_month} onChange={(e) => setUserForm({ ...userForm, nameday_month: e.target.value })} placeholder="Month" className="bg-white/5 border-white/10 text-white" />
                </div>
                {namedayLookup?.options?.length > 1 && (
                  <div className="space-y-2">
                    {namedayLookup.options.map((option) => (
                      <button
                        key={`${option.id}-${option.date}`}
                        type="button"
                        onClick={() => setUserForm({ ...userForm, nameday_day: option.day, nameday_month: option.month, nameday_confirmed: true, nameday_source: 'admin_selected', nameday_date_rule: option, nameday_options: namedayLookup.options || [] })}
                        className="block w-full rounded-sm border border-white/10 bg-black/20 p-2 text-left text-sm text-gray-200"
                      >
                        {new Date(option.date).toLocaleDateString('el-GR', { day: 'numeric', month: 'long' })} - {option.label_el || option.canonical_name}
                      </button>
                    ))}
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={userForm.nameday_confirmed} onChange={(e) => setUserForm({ ...userForm, nameday_confirmed: e.target.checked })} />
                  Nameday confirmed
                </label>
              </div>
              <div className="flex justify-end">
                <Button onClick={saveUser} disabled={saving} className="bg-primary hover:bg-primary/90">
                  <Save size={16} className="mr-2" />
                  Save Details
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="access" className="space-y-4 mt-6">
              <div>
                <label className="text-sm text-gray-400">Access expires at (optional)</label>
                <Input type="date" value={accessExpiresAt} onChange={(e) => setAccessExpiresAt(e.target.value)} className="bg-white/5 border-white/10 text-white max-w-xs mt-1" />
              </div>
              {categories.map((category) => (
                <div key={category.id} className="bg-white/5 rounded-md">
                  <label className="flex items-center gap-3 p-3 border-b border-white/10">
                    <input type="checkbox" checked={!!categoryAccess[category.id]} onChange={(e) => toggleCategory(category.id, e.target.checked)} />
                    <span className="text-white font-medium">{titleFor(category)}</span>
                  </label>
                  <div className="p-3 pl-8 space-y-2">
                    {videos.filter((video) => video.category_id === category.id).map((video) => (
                      <label key={video.id} className="flex items-center gap-3 text-sm text-gray-300">
                        <input type="checkbox" checked={!!videoAccess[video.id]} onChange={(e) => setVideoAccess((prev) => ({ ...prev, [video.id]: e.target.checked }))} />
                        {titleFor(video)}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <Button onClick={saveAccess} disabled={saving} className="bg-primary hover:bg-primary/90">Save Access</Button>
              </div>
            </TabsContent>

            <TabsContent value="subscription" className="space-y-4 mt-6">
              {userData?.subscription && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-md p-3 text-green-300">
                  Active: {userData.subscription.plan_name || userData.subscription.plan_id}
                  {userData.subscription.provider && (
                    <span className="block text-xs text-green-200/80 mt-1">
                      Provider: {userData.subscription.provider}
                    </span>
                  )}
                </div>
              )}
              {paidSubscriptionReadOnly ? (
                <div className="bg-white/5 border border-white/10 rounded-md p-4 text-sm text-gray-300">
                  Paid Stripe/Revolut subscriptions are read-only here. Use provider-specific payment tools for paid subscription changes.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <select value={subscriptionForm.plan_id} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, plan_id: e.target.value })} className="bg-[#0a0a0a] border border-white/10 text-white rounded-md px-3 py-2">
                      <option value="">Select plan...</option>
                      {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
                    </select>
                    <label className="flex items-center gap-2 text-white">
                      <input type="checkbox" checked={subscriptionForm.is_free} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, is_free: e.target.checked })} />
                      Free manual subscription
                    </label>
                    <Input type="date" value={subscriptionForm.start_date} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, start_date: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                    <Input type="date" value={subscriptionForm.expires_at} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, expires_at: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                  </div>
                  <textarea value={subscriptionForm.notes} onChange={(e) => setSubscriptionForm({ ...subscriptionForm, notes: e.target.value })} placeholder="Notes" className="w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 min-h-[70px]" />
                  <div className="flex justify-between">
                    {userData?.subscription?.provider === 'admin_manual' ? (
                      <Button variant="outline" onClick={cancelSubscription} className="border-red-500/50 text-red-400 hover:bg-red-500/10">
                        <Trash2 size={16} className="mr-2" />
                        Cancel Manual Subscription
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-500">Manual subscriptions are stored as admin_manual only.</span>
                    )}
                    <Button onClick={saveSubscription} disabled={saving} className="bg-primary hover:bg-primary/90">Save Subscription</Button>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="analytics" className="space-y-4 mt-6">
              {!analytics ? (
                <p className="text-gray-500 text-center py-6">No analytics available</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-white/5 rounded-md p-3">
                      <p className="text-xs text-gray-500">Views</p>
                      <p className="text-xl font-bold text-white">{analytics.total_views || 0}</p>
                    </div>
                    <div className="bg-white/5 rounded-md p-3">
                      <p className="text-xs text-gray-500">Watch minutes</p>
                      <p className="text-xl font-bold text-white">{analytics.total_watch_minutes || 0}</p>
                    </div>
                    <div className="bg-white/5 rounded-md p-3">
                      <p className="text-xs text-gray-500">Completed</p>
                      <p className="text-xl font-bold text-white">{analytics.completed_videos || 0}</p>
                    </div>
                    <div className="bg-white/5 rounded-md p-3">
                      <p className="text-xs text-gray-500">Points</p>
                      <p className="text-xl font-bold text-primary">{analytics.points_total || 0}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white/5 rounded-md p-4">
                      <h3 className="text-white font-medium mb-3">Badges earned</h3>
                      {(analytics.badges || []).length > 0 ? (
                        <div className="space-y-2">
                          {analytics.badges.slice(0, 8).map((badge) => (
                            <div key={badge.id} className="flex justify-between gap-3 text-sm">
                              <span className="text-gray-200">{badge.name_el || badge.name_en || badge.name || badge.badge_slug}</span>
                              <span className="text-gray-500">{badge.earned_at?.slice(0, 10)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No badges earned yet</p>
                      )}
                    </div>

                    <div className="bg-white/5 rounded-md p-4">
                      <h3 className="text-white font-medium mb-3">Referral rewards</h3>
                      {(analytics.referral_rewards || []).length > 0 ? (
                        <div className="space-y-2">
                          {analytics.referral_rewards.slice(0, 8).map((reward) => (
                            <div key={reward.id} className="flex justify-between gap-3 text-sm">
                              <span className="text-gray-200">{reward.referred_email || reward.referred_user_id || 'Referral'}</span>
                              <span className="text-green-400">{reward.reward_type || 'rewarded'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No referral rewards</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-md p-4">
                    <h3 className="text-white font-medium mb-3">Per-video watch time</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {(analytics.per_video_watch || []).length > 0 ? analytics.per_video_watch.map((row) => (
                        <div key={row.video_id} className="border border-white/10 rounded-md p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-white text-sm">{row.video_title || row.video_id}</p>
                              <p className="text-xs text-gray-500">
                                {row.total_watch_seconds || 0}s watched · max {row.max_progress_seconds || 0}s · {row.view_count || 0} views
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${row.completed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {row.completed ? 'Completed' : 'In progress'}
                            </span>
                          </div>
                        </div>
                      )) : (
                        <p className="text-sm text-gray-500 text-center py-4">No watch history</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <div className="space-y-2">
                {(userData?.payment_history || []).map((payment) => (
                  <div key={payment.id || payment.session_id} className="flex justify-between bg-white/5 rounded-md p-3 text-sm">
                    <span className="text-white">{payment.payment_type || 'payment'} - {payment.payment_status || payment.status}</span>
                    <span className="text-gray-400">{payment.amount} {payment.currency}</span>
                  </div>
                ))}
                {(!userData?.payment_history || userData.payment_history.length === 0) && (
                  <p className="text-gray-500 text-center py-6">No payment history</p>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserEditDialog;
